import { contextRange, passageUnitOf } from '@versesverse/core';
import type { PassageTables } from './passages';

/*
 * THE PASSAGE AROUND A VERSE — which ids sit either side of it, and how far that runs.
 *
 * A verse quoted alone is the thing this project is most often accused of doing to scripture, so any
 * verse opens into the passage it sits in. Verse ids are positions in reading order, so the
 * neighbours are the ids on each side — but only within the same unit, because running off the end
 * of Malachi into Matthew is not context. `contextRange` and `passageUnitOf` (both in core) do the
 * work; this binds them to the bundle's own reference and corpus tables. Pure, so it can be tested
 * without the fetch that turns these ids into text.
 */
type UnitTables = Pick<PassageTables, 'refs' | 'corpusOf' | 'corpora'>;

/** The passage-unit of a verse id — its book, or its chapter/surah where the text is bounded that way. */
export function unitResolver(tables: UnitTables): (id: number) => string {
  const byCode = new Map(tables.corpora.map(c => [c.code, c]));
  return (id) => {
    const corpus = byCode.get(tables.corpusOf[id] ?? '');
    /* passageUnit defaults to `book`, the bound for every text that does not say otherwise. */
    return passageUnitOf(corpus ? { passageUnit: corpus.passageUnit ?? 'book' } : undefined, tables.refs[id] ?? '');
  };
}

/**
 * The verse ids of the passage around `center`, in reading order — `[before…, center, after…]` — with
 * `before` verses sought above and `after` below, EACH side sought independently and stopping at the
 * unit boundary. The two sides are separate so the expanded reader can grow one without the other: when
 * you scroll up it loads only prior verses (nothing appears below to shove your place), which is what
 * keeps scroll-to-read-more from jumping.
 */
export function contextWindow(center: number, before: number, after: number, tables: UnitTables): number[] {
  const unitAt = unitResolver(tables);
  const count = tables.refs.length;
  const back = contextRange(center, before, unitAt, count).before;
  const forward = contextRange(center, after, unitAt, count).after;
  return [...back, center, ...forward];
}
